import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import CommonImage from '@/components/CommonImage';
import { useGetMyDashboardTasksQuery } from '@/features/task-configuration/api/taskConfigurationApi';
import DashboardCard from '@/features/task-configuration/components/DashboardCard';
import DashboardSectionTab from '@/features/task-configuration/components/DashboardSectionTab';
import NoData from '@/components/NoData';
import LoaderComponent from '@/components/LoaderComponent';
import MyDashboardNotification from '@/features/my-dashborad/components/MyDashboardNotification';
import { DASHBOARD_SECTIONS, DASHBOARD_TAB_ITEMS } from '@/features/task-configuration/constants';
import MyDashboardStyles from '@/features/my-dashborad/components/MyDashboardStyles';
import {
	useAddPersonalSettingMutation,
	useGetPersonalSettingsQuery,
	useGetTaskListCountQuery
} from '@/api/commonApis';

const MyDashboard = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	const {
		data: myDashboardTasks = {},
		isLoading: isLoadingMyDashboardTasks,
		isFetching: isFetchingMyDashboardTasks,
		refetch
	} = useGetMyDashboardTasksQuery(undefined, {
		refetchOnMountOrArgChange: false,
		refetchOnFocus: false,
		refetchOnReconnect: true
	});

	// Derive current tab from URL params
	const currentTab = searchParams.get('tab') || DASHBOARD_TAB_ITEMS.OTHERS_PENDING_TASK;

	// Determine if showing MyDashboardStyles based on URL tab
	const isShowingStyles = currentTab === DASHBOARD_TAB_ITEMS.MY_DASHBOARD_STYLES;

	const [isExpanded, setIsExpanded] = useState(true);
	const [isToggleModel, setIsToggleModel] = useState(null);

	const [addPersonalSetting, {}] = useAddPersonalSettingMutation();

	// Fetch saved accordion state
	const { data: savedSettings } = useGetPersonalSettingsQuery({
		settingKey: 'MY_DASHBOARD_UI_STATE'
	});

	// Apply saved accordion state on component mount
	useEffect(() => {
		if (savedSettings?.value) {
			try {
				const parsedState = JSON.parse(savedSettings.value);
				if (typeof parsedState?.isExpanded === 'boolean') {
					setIsExpanded(parsedState.isExpanded);
				}
			} catch {
				// Error parsing saved accordion state - will use default state
			}
		}
	}, [savedSettings]);

	const toggleModel = (id) => {
		setIsToggleModel(id);
	};

	const handleSavePendingTaskAccordion = (expandedState) => {
		addPersonalSetting({
			body: {
				key: 'MY_DASHBOARD_UI_STATE',
				value: JSON.stringify({ isExpanded: expandedState })
			}
		});
	};

	// Wrapper function to handle accordion state change and save
	const handleSetIsExpanded = (newExpandedState) => {
		setIsExpanded((prevState) => {
			// Handle functional update pattern: if newExpandedState is a function, call it with prevState
			const nextState =
				typeof newExpandedState === 'function' ? newExpandedState(prevState) : newExpandedState;

			// Save the new state to API
			handleSavePendingTaskAccordion(nextState);

			return nextState;
		});
	};

	const groupedDashboardCards = useMemo(() => {
		const grouped = {};

		// Initialize groups for all sections
		DASHBOARD_SECTIONS.forEach((section) => {
			grouped[section.id] = {
				sectionInfo: section,
				cards: []
			};
		});

		// Group cards by their section constant
		myDashboardTasks?.cardResponses?.forEach((card) => {
			const sectionConstant = card?.section?.constant;
			if (sectionConstant && grouped[sectionConstant]) {
				grouped[sectionConstant].cards.push(card);
			}
		});

		return grouped;
	}, [myDashboardTasks]);

	const pendingTaskCount = myDashboardTasks?.pendingTaskCount ?? 0;
	const completedTaskCount = myDashboardTasks?.completedTaskCount ?? 0;

	const handleClickFilterItem = (data) => {
		// Update URL to show styles view with filterId
		setSearchParams(
			{ tab: DASHBOARD_TAB_ITEMS.MY_DASHBOARD_STYLES, filterId: data.id, filterTitle: data?.title },
			{ replace: true }
		);
	};

	const handleTabChange = (newTab) => {
		setSearchParams({ tab: newTab }, { replace: true });
	};

	const {
		data: dashboardTaskCount,
		isLoading: isLoadingDashboardTaskCount,
		isFetching: isFetchingDashboardTaskCount
	} = useGetTaskListCountQuery(undefined, {
		refetchOnMountOrArgChange: false,
		refetchOnFocus: false,
		refetchOnReconnect: true
	});

	const isDashboardTaskCountLoading = isLoadingDashboardTaskCount || isFetchingDashboardTaskCount;

	return (
		<LoaderComponent loading={isLoadingMyDashboardTasks || isFetchingMyDashboardTasks}>
			{isShowingStyles ? (
				<MyDashboardStyles />
			) : (
				<div className="body-content pending-task-container bg-white">
					<div className="pending-task-main-wrapper d-flex">
						{/* Left Section - Main Content */}
						<div className="pending-task-left-section flex-grow-1">
							<div className="pending-task-header bg-white d-flex align-items-center justify-content-between">
								<div className="pending-task-summary">
									<p className="semibold-14 gray_dark_01">
										My Pending Tasks:
										<span className="gray_dark_01 ml-1"> ({pendingTaskCount} )</span>
										<span className="gray_dark_02 ml-1 regular-14 weight-400">
											| Today completed:
											<span className="ml-1"> ({completedTaskCount}) </span>
										</span>
									</p>
								</div>
								<div className="pending-task-refresh" onClick={refetch}>
									<span className="regular-14 gray_dark_02 weight-400 d-flex align-items-center cursor-pointer">
										<span className="mr-2">Refresh</span>
										<CommonImage
											src="/icons/load.svg"
											width={16}
											height={16}
											alt="Refresh"
										/>
									</span>
								</div>
							</div>
							{/* Task Categories Grid */}
							<div className="running-task-body d-flex justify-content-between align-items-start">
								{/* right Section - Main Content Dashboad Cards */}
								<div className="pending-task-section flex-grow-1">
									{/* Pending Tasks Sections - Display at Top */}
									<div className="all-tasks scroll-y-label">
										{groupedDashboardCards['MY_PENDING_TASKS']?.cards?.length > 0 ? (
											<div className="task-categories d-grid gap-4 grid-columns-3">
												{groupedDashboardCards['MY_PENDING_TASKS']?.cards?.map(
													(card) => (
														<DashboardCard
															key={card.id}
															card={card}
															isReadOnly
															onClickFilterItem={handleClickFilterItem}
															isShowCount={true}
															shouldDisableFilter={true}
															taskListCount={dashboardTaskCount}
															isLoadingDashboardTaskCount={
																isDashboardTaskCountLoading
															}
														/>
													)
												)}
											</div>
										) : (
											<NoData message={`There is no Pending task at this moment`} />
										)}
									</div>
								</div>
							</div>
						</div>

						{/* Right Section - Notification Mentions Sidebar */}
						<MyDashboardNotification toggleModel={toggleModel} isToggleModel={isToggleModel} />
					</div>
					{/**-------- Tab Wrapper for Other Sections and compleated sections -----------**/}
					<div className="other-tasks-section mt-3">
						<div className="other-tasks-header bg-white">
							<div className="navigation-tab bg-white">
								<ul>
									<li
										className={`${currentTab === DASHBOARD_TAB_ITEMS.OTHERS_PENDING_TASK ? 'active' : ''}`}
										onClick={() =>
											handleTabChange(DASHBOARD_TAB_ITEMS.OTHERS_PENDING_TASK)
										}
									>
										<p>Pending with Others</p>
									</li>
									<li
										className={`${currentTab === DASHBOARD_TAB_ITEMS.COMPLETED_TASK ? 'active' : ''}`}
										onClick={() => handleTabChange(DASHBOARD_TAB_ITEMS.COMPLETED_TASK)}
									>
										<p>Completed task</p>
									</li>
								</ul>
							</div>
						</div>
						{/* Other pending task tab */}
						{currentTab === DASHBOARD_TAB_ITEMS.OTHERS_PENDING_TASK &&
							(groupedDashboardCards['OTHERS_PENDING_TASKS']?.cards?.length > 0 ? (
								<DashboardSectionTab
									isExpanded={isExpanded}
									setIsExpanded={handleSetIsExpanded}
									cards={groupedDashboardCards['OTHERS_PENDING_TASKS']?.cards || []}
									isReadOnly
									isShowCount={true}
									shouldDisableFilter={true}
									taskListCount={dashboardTaskCount}
									isLoadingDashboardTaskCount={isDashboardTaskCountLoading}
								/>
							) : (
								<NoData message={`There is no Others pending task at this moment`} />
							))}
						{/* Completed task tab */}
						{currentTab === DASHBOARD_TAB_ITEMS.COMPLETED_TASK &&
							(groupedDashboardCards['COMPLETED_TASKS']?.cards?.length > 0 ? (
								<DashboardSectionTab
									isExpanded={isExpanded}
									setIsExpanded={handleSetIsExpanded}
									cards={groupedDashboardCards['COMPLETED_TASKS']?.cards || []}
									isReadOnly
									isShowCount={true}
									shouldDisableFilter={true}
									taskListCount={dashboardTaskCount}
									isLoadingDashboardTaskCount={isDashboardTaskCountLoading}
								/>
							) : (
								<NoData message={`There is no Completed task  at this moment`} />
							))}
					</div>
				</div>
			)}
		</LoaderComponent>
	);
};

export default MyDashboard;
